import { i18n } from "../i18n";

export interface BackupDirectory {
  name: string;
  getFileHandle(
    name: string,
    options?: { create?: boolean },
  ): Promise<{
    getFile(): Promise<Blob>;
    createWritable(options: { keepExistingData: false; mode: "exclusive" }): Promise<{
      write(data: Blob): Promise<void>;
      close(): Promise<void>;
      abort(): Promise<void>;
    }>;
  }>;
}

type PickerWindow = Window & {
  showDirectoryPicker?: (options: { mode: "readwrite" }) => Promise<BackupDirectory>;
};

export class BackupFolderError extends Error {
  constructor(public readonly code: "unsupported" | "permission" | "collision" | "invalid" | "write") {
    super(code);
  }
}

export function supportsBackupDirectory(): boolean {
  return window.isSecureContext && typeof (window as PickerWindow).showDirectoryPicker === "function";
}

// Called directly from a user click, before awaiting snapshot creation or encryption.
export function selectBackupDirectory(): Promise<BackupDirectory> {
  const pickerWindow = window as PickerWindow;
  if (!supportsBackupDirectory() || !pickerWindow.showDirectoryPicker) {
    return Promise.reject(new BackupFolderError("unsupported"));
  }
  return pickerWindow.showDirectoryPicker({ mode: "readwrite" });
}

function hasOnlyKeys(value: unknown, keys: string[]): boolean {
  return (
    typeof value === "object" &&
    value !== null &&
    Object.keys(value).length === keys.length &&
    Object.keys(value).every((key) => keys.includes(key))
  );
}

function envelopeDate(encrypted: string): string {
  try {
    const value = JSON.parse(encrypted);
    const keys = ["format", "formatVersion", "createdAt", "kdf", "cipher", "ciphertext"];
    if (
      !value ||
      Object.keys(value).length !== keys.length ||
      Object.keys(value).some((key) => !keys.includes(key)) ||
      value.format !== "rollclassbackup" ||
      value.formatVersion !== 1 ||
      typeof value.createdAt !== "string" ||
      !Number.isFinite(Date.parse(value.createdAt)) ||
      !hasOnlyKeys(value.kdf, ["name", "hash", "iterations", "salt"]) ||
      !hasOnlyKeys(value.cipher, ["name", "iv"]) ||
      value.kdf?.name !== "PBKDF2" ||
      value.cipher?.name !== "AES-GCM" ||
      typeof value.ciphertext !== "string" ||
      !value.ciphertext.length
    )
      throw new Error("invalid");
    return new Date(value.createdAt).toISOString().slice(0, 10);
  } catch {
    throw new BackupFolderError("invalid");
  }
}

export async function saveBackupToDirectory(
  directory: BackupDirectory,
  encrypted: string,
  signal: AbortSignal,
): Promise<string> {
  const date = envelopeDate(encrypted);
  const blob = new Blob([encrypted], { type: "application/octet-stream" });
  if (blob.size > 100 * 1024 * 1024) throw new BackupFolderError("invalid");
  signal.throwIfAborted();
  try {
    for (let attempt = 0; attempt < 3; attempt++) {
      const filename = `${i18n.t("brand:backupFilenamePrefix")}-${date}-${crypto.randomUUID()}.rollclassbackup`;
      try {
        await directory.getFileHandle(filename);
        continue; // A zero-byte file is also an existing backup; never open it for writing.
      } catch (caught) {
        if (!(caught instanceof DOMException && caught.name === "NotFoundError")) throw caught;
      }
      signal.throwIfAborted();
      const handle = await directory.getFileHandle(filename, { create: true });
      if ((await handle.getFile()).size !== 0) continue;
      signal.throwIfAborted();
      const stream = await handle.createWritable({ keepExistingData: false, mode: "exclusive" });
      try {
        signal.throwIfAborted();
        await stream.write(blob);
        signal.throwIfAborted();
        await stream.close();
      } catch (caught) {
        await stream.abort().catch(() => undefined);
        throw caught;
      }
      // A successful local write is not proof that a cloud client uploaded the file.
      if ((await (await handle.getFile()).text()) !== encrypted) throw new BackupFolderError("write");
      return filename;
    }
    throw new BackupFolderError("collision");
  } catch (caught) {
    if (caught instanceof BackupFolderError || (caught instanceof DOMException && caught.name === "AbortError"))
      throw caught;
    if (caught instanceof DOMException && ["NotAllowedError", "SecurityError"].includes(caught.name)) {
      throw new BackupFolderError("permission");
    }
    throw new BackupFolderError("write");
  }
}
